import React, { useState, useEffect } from 'react';
import './GameHistory.css';
import { api } from '../utils/api';
import { VERSION } from '../version';

const GameHistory = ({ gameState, gameData, board, onBackToMenu, onBackToSetup, onReplayToMove, onResign, onSaveAndExit, currentMoveIndex = null }) => {
  const [databaseMoves, setDatabaseMoves] = useState([]);
  const [loadingMoves, setLoadingMoves] = useState(false);
  const [serverVersion, setServerVersion] = useState('checking...');

  // Manual refresh function for debugging
  const refreshMoves = async () => {
    if (!gameData?.id) {
      return;
    }
    
    setLoadingMoves(true);
    try {
      const moves = await api.getGameMoves(gameData.id);
      setDatabaseMoves(moves);
    } catch (error) {
      console.error('Manual refresh failed:', error);
    } finally {
      setLoadingMoves(false);
    }
  };

  // Fetch database moves when gameData changes
  useEffect(() => {
    const fetchMoves = async () => {
      if (!gameData?.id) {
        return;
      }

      setLoadingMoves(true);
      try {
        const moves = await api.getGameMoves(gameData.id);
        
        setDatabaseMoves(moves);
      } catch (error) {
        console.error('❌ GameHistory: Failed to fetch moves:', error);
        setDatabaseMoves([]);
      } finally {
        setLoadingMoves(false);
      }
    };

    fetchMoves();
  }, [gameData?.id, gameState?.moveHistory?.length]); // Also refresh when move history changes

  // Fetch server version on mount
  useEffect(() => {
    const fetchServerVersion = async () => {
      try {
        const { SERVER_HOST } = await import('../utils/apiBase');
        const response = await fetch(`${SERVER_HOST}/health`);
        const data = await response.json();
        setServerVersion(data.version || 'unknown');
      } catch (error) {
        console.error('Failed to fetch server version:', error);
        setServerVersion('error');
      }
    };
    fetchServerVersion();
  }, []);

  // Convert board coordinates to algebraic notation
  const coordsToAlgebraic = (row, col) => {
    const files = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i'];
    const ranks = ['1', '2', '3', '4', '5', '6', '7', '8', '9']; // Row 0 = rank 1, Row 8 = rank 9
    
    if (row === undefined || col === undefined) {
      return '??';
    }
    
    const result = files[col] + ranks[row];
    return result;
  };

  // Convert piece type to algebraic notation
  const pieceToAlgebraic = (piece) => {
    const pieceNotation = {
      'king': 'K',
      'knight': 'N',
      'pawn': '',
      'advisor': 'A',
      'cannon': 'C',
      'camel': 'M',
      'donkey': 'D',
      'ram': 'R',
      'spy': 'S'
    };
    return pieceNotation[piece] || '';
  };

  // Convert move to algebraic notation
  const moveToAlgebraic = (move, index) => {
    // Use pre-generated algebraic notation from database if available
    if (move.algebraic_notation) {
      return move.algebraic_notation;
    }
    
    // Fallback to generating notation from coordinates
    
    // Handle both database move format and game move format
    const pieceType = move.piece_type || move.piece;
    const piece = pieceToAlgebraic(pieceType);
    const fromRow = move.from_row || move.from?.row;
    const fromCol = move.from_col || move.from?.col;
    const toRow = move.to_row || move.to?.row;
    const toCol = move.to_col || move.to?.col;
    
    const from = coordsToAlgebraic(fromRow, fromCol);
    const to = coordsToAlgebraic(toRow, toCol);
    
    // Check if this is a ram bump (use '-' instead of 'x')
    let capture = '';
    const captured = move.captured_piece || move.captured;
    if (captured) {
      if (move.is_ram_bump && pieceType === 'ram') {
        capture = '-'; // Ram bump notation
      } else {
        capture = 'x'; // Regular capture notation
      }
    }
    
    // For pawns, show file when capturing
    if (pieceType === 'pawn' && capture) {
      return `${from[0]}${capture}${to}`;
    }
    
    const result = `${piece}${from}${capture}${to}`;
    return result;
  };

  // Export board position to ASCII format
  const exportBoardPosition = () => {
    if (!board) {
      alert('No board position to export');
      return;
    }
    
    // Piece mapping from internal format to ASCII
    const pieceToAscii = {
      king: { white: 'K', black: 'k' },
      advisor: { white: 'A', black: 'a' },
      camel: { white: 'M', black: 'm' },
      knight: { white: 'N', black: 'n' },
      donkey: { white: 'D', black: 'd' },
      cannon: { white: 'C', black: 'c' },
      ram: { white: 'R', black: 'r' },
      spy: { white: 'S', black: 's' },
      pawn: { white: 'P', black: 'p' }
    };
    
    let asciiBoard = '';
    
    // Generate the board from rank 9 to 1 (black pieces at top, white pieces at bottom)
    for (let rank = 9; rank >= 1; rank--) {
      const row = rank - 1; // Convert rank to array index (rank 1 = row 0)
      let line = rank.toString();
      
      for (let col = 0; col < 9; col++) {
        const piece = board[row] && board[row][col];
        let ascii = '';
        
        if (piece && pieceToAscii[piece.type]) {
          ascii = pieceToAscii[piece.type][piece.color];
        }
        
        line += '\t' + ascii;
      }
      
      asciiBoard += line + '\n';
    }
    
    // Add file labels
    asciiBoard += '\ta\tb\tc\td\te\tf\tg\th\ti\n';
    
    // Create and download file
    const blob = new Blob([asciiBoard], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `board_position_${new Date().toISOString().slice(0, 10)}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  // Export game history to CSV
  const exportGameHistory = () => {
    if (!databaseMoves || databaseMoves.length === 0) {
      alert('No game history to export');
      return;
    }

    const moves = databaseMoves;
    const csvData = [];
    
    // Add header
    csvData.push(['Move Number', 'Player', 'Piece', 'From', 'To', 'Captured', 'Algebraic Notation']);
    
    // Add game metadata
    csvData.push(['Game Info', '', '', '', '', '', '']);
    csvData.push(['White Player', gameData?.whitePlayerName || 'Unknown', '', '', '', '', '']);
    csvData.push(['Black Player', gameData?.blackPlayerName || 'Unknown', '', '', '', '', '']);
    csvData.push(['Game Mode', gameData?.gameMode || 'Unknown', '', '', '', '', '']);
    csvData.push(['Export Date', new Date().toISOString(), '', '', '', '', '']);
    csvData.push(['', '', '', '', '', '', '']); // Empty row
    
    // Add moves
    moves.forEach((move, index) => {
      const moveNumber = Math.floor(index / 2) + 1;
      const player = move.player_color || (index % 2 === 0 ? 'white' : 'black');
      const fromSquare = coordsToAlgebraic(move.from_row, move.from_col);
      const toSquare = coordsToAlgebraic(move.to_row, move.to_col);
      const capturedPiece = move.captured_piece || '';
      const algebraicNotation = moveToAlgebraic(move, index);
      
      csvData.push([
        moveNumber,
        player,
        move.piece_type,
        fromSquare,
        toSquare,
        capturedPiece,
        algebraicNotation
      ]);
    });
    
    // Convert to CSV string
    const csvString = csvData.map(row => 
      row.map(cell => `"${cell}"`).join(',')
    ).join('\n');
    
    // Create and download file
    const blob = new Blob([csvString], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    // Generate filename with date and player names
    const dateStr = new Date().toISOString().split('T')[0];
    const whitePlayer = gameData?.whitePlayerName?.replace(/[^a-zA-Z0-9]/g, '') || 'White';
    const blackPlayer = gameData?.blackPlayerName?.replace(/[^a-zA-Z0-9]/g, '') || 'Black';
    link.download = `cannons_game_${whitePlayer}_vs_${blackPlayer}_${dateStr}.csv`;
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const renderMoveHistory = () => {
    // Debug logs removed for performance (these were causing infinite render loops)
    
    if (loadingMoves) {
      return <div className="loading">Loading moves...</div>;
    }
    
    if (!databaseMoves || databaseMoves.length === 0) {
      // Fallback to gameState.moveHistory if database moves are not available
      if (gameState && gameState.moveHistory && gameState.moveHistory.length > 0) {
        const moves = gameState.moveHistory;
        const moveRows = [];
        
        for (let i = 0; i < moves.length; i += 2) {
          const whiteMove = moves[i];
          const blackMove = moves[i + 1];
          const moveNumber = Math.floor(i / 2) + 1;
          
          // Format think time for tooltip
          const whiteThinkTime = whiteMove?.think_time 
            ? (whiteMove.think_time < 1000 
                ? `${whiteMove.think_time.toFixed(0)}ms` 
                : `${(whiteMove.think_time / 1000).toFixed(1)}s`)
            : null;
          const blackThinkTime = blackMove?.think_time 
            ? (blackMove.think_time < 1000 
                ? `${blackMove.think_time.toFixed(0)}ms` 
                : `${(blackMove.think_time / 1000).toFixed(1)}s`)
            : null;

          moveRows.push(
            <div key={moveNumber} className="move-row">
              <span className="move-number">{moveNumber}.</span>
              <span 
                className={`white-move clickable-move ${currentMoveIndex === i ? 'current-move' : ''}`}
                onClick={() => onReplayToMove && onReplayToMove(i)}
                data-think-time={whiteThinkTime || undefined}
              >
                {whiteMove ? moveToAlgebraic(whiteMove, i) : ''}
              </span>
              <span 
                className={`black-move clickable-move ${currentMoveIndex === i + 1 ? 'current-move' : ''}`}
                onClick={() => onReplayToMove && blackMove && onReplayToMove(i + 1)}
                data-think-time={blackThinkTime || undefined}
              >
                {blackMove ? moveToAlgebraic(blackMove, i + 1) : ''}
              </span>
            </div>
          );
        }
        
        return moveRows;
      }
      
      return <div className="no-moves">No moves yet</div>;
    }
    
    const moveRows = [];
    
    // Group moves by move number (white and black moves)
    for (let i = 0; i < databaseMoves.length; i += 2) {
      const whiteMove = databaseMoves[i];
      const blackMove = databaseMoves[i + 1];
      const moveNumber = Math.floor(i / 2) + 1;
      
      moveRows.push(
        <div key={moveNumber} className="move-row">
          <span className="move-number">{moveNumber}.</span>
          <span 
            className={`white-move clickable-move ${currentMoveIndex === i ? 'current-move' : ''}`}
            onClick={() => onReplayToMove && onReplayToMove(i)}
          >
            {whiteMove ? moveToAlgebraic(whiteMove, i) : ''}
          </span>
          <span 
            className={`black-move clickable-move ${currentMoveIndex === i + 1 ? 'current-move' : ''}`}
            onClick={() => onReplayToMove && blackMove && onReplayToMove(i + 1)}
          >
            {blackMove ? moveToAlgebraic(blackMove, i + 1) : ''}
          </span>
        </div>
      );
    }
    
    return moveRows;
  };

  return (
    <div className="game-history">
      <div className="history-header">
        <h3>Game History</h3>
        <div style={{fontSize: '12px', color: '#666'}}>
          Client: v{VERSION} | Server: v{serverVersion}
          {VERSION !== serverVersion && serverVersion !== 'checking...' && (
            <span style={{color: 'red', fontWeight: 'bold'}}> ⚠️ MISMATCH</span>
          )}
          <br/>
          Game #{gameData?.id || '?'} | {databaseMoves?.length || 0} moves
          <button onClick={refreshMoves} style={{marginLeft: '10px', fontSize: '10px'}}>
            Refresh
          </button>
        </div>
        <div className="history-controls">
          <button className="export-button" onClick={exportGameHistory}>
            📥 Export CSV
          </button>
          <button className="export-button" onClick={exportBoardPosition}>
            📄 Export Position
          </button>
          {onSaveAndExit && gameData?.id && !gameState?.gameOver && (
            <button className="save-exit-button" onClick={onSaveAndExit}>
              💾 Save & Exit
            </button>
          )}
          <button className="back-button" onClick={onBackToSetup}>
            ← Setup
          </button>
          <button className="menu-button" onClick={onBackToMenu}>
            🏠 Menu
          </button>
        </div>
      </div>
      
      {gameState && gameState.moveHistory && gameState.moveHistory.length > 0 && (
        <div className="history-navigation">
          <button 
            className="nav-button" 
            onClick={() => onReplayToMove && onReplayToMove(-1)}
            disabled={currentMoveIndex === -1}
            title="Go to start"
          >
            ⏮️
          </button>
          <button 
            className="nav-button" 
            onClick={() => onReplayToMove && onReplayToMove(Math.max(-1, (currentMoveIndex ?? gameState.moveHistory.length) - 1))}
            disabled={currentMoveIndex === -1}
            title="Previous move"
          >
            ⏪
          </button>
          <button 
            className="nav-button" 
            onClick={() => onReplayToMove && onReplayToMove(Math.min(gameState.moveHistory.length - 1, (currentMoveIndex ?? -1) + 1))}
            disabled={currentMoveIndex === gameState.moveHistory.length - 1}
            title="Next move"
          >
            ⏩
          </button>
          <button 
            className="nav-button" 
            onClick={() => onReplayToMove && onReplayToMove(gameState.moveHistory.length - 1)}
            disabled={currentMoveIndex === gameState.moveHistory.length - 1}
            title="Go to end"
          >
            ⏭️
          </button>
        </div>
      )}
      
      <div className="history-container">
        {renderMoveHistory()}
      </div>
      
      {onResign && gameState && !gameState.gameOver && (
        <button className="resign-button" onClick={onResign}>
          🏳️ Resign
        </button>
      )}
    </div>
  );
};

export default GameHistory; 